/* eslint-disable @typescript-eslint/member-ordering */
import {computed, makeObservable, observable} from "mobx";
import {JsonObject, JsonProperty} from "typescript-json-serializer";
import {ProjectProcess} from "./project-process";
import {Municipality} from "./municipality";
import {Contractor} from "./contractor";
import {AbstractModel} from "./abstract-model";
import {booleanToEsri, esriToBoolean} from "../utils/esri-utils";

export interface ProjectProps {
    objectId?: number;
    guid?: string;
    id?: number;
    uuid?: string;
    name?: string;
    typeId?: number;
    reason?: string;
    contractor?: string;
    contractorId?: number;
    statusId?: number;
    gkoa?: boolean;
    ghkw?: boolean;
    creator?: string;
    createdAt?: Date;
    editor?: string;
    updatedAt?: Date;
    municipalities?: Municipality[];
}

/**
 * Projekt
 */
@JsonObject()
export class Project extends AbstractModel {

    static readonly comparableAttributes: string[] = [
        "id", "uuid", "name", "typeId", "reason", "contractorId", "gkoa", "ghkw"
    ];
    // workaround, because feature template doesn't seem to work for table features
    static readonly prototypeAttributes: string[] = [
        "OBJECTID", "GUID", "PROJ_ID", "PROJ_UUID", "PROJ_NAME", "PROJ_TYP", "PROJ_GRUND", "AUFT_NEHM", "STATUS",
        "GKOA", "GHKW",
        "CREATED_USER", "CREATED_AT", "UPDATED_USER", "UPDATED_AT"
    ];
    // field mappings
    static readonly databaseFieldNames: Map<string, string> = new Map<string, string>([
        ["objectId", "OBJECTID"],
        ["guid", "GUID"],
        ["id", "PROJ_ID"],
        ["uuid", "PROJ_UUID"],
        ["name", "PROJ_NAME"],
        ["typeId", "PROJ_TYP"],
        ["reason", "PROJ_GRUND"],
        ["contractor", "AUFT_NEHM"],
        ["contractorId", "AUFTRAGNEHMER_ID"],
        ["statusId", "STATUS"],
        ["gkoa", "GKOA"],
        ["ghkw", "GHKW"],
        ["creator", "CREATED_USER"],
        ["createdAt", "CREATED_AT"],
        ["editor", "UPDATED_USER"],
        ["updatedAt", "UPDATED_AT"]
    ]);

    class = "Project";
    typeLabel = "Projekt";

    static sortByDate(a: Project, b: Project) {
        if (a?.createdAt && b?.createdAt && a?.createdAt > b?.createdAt) {
            return -1;
        }
        if (a?.createdAt && b?.createdAt && a?.createdAt < b?.createdAt) {
            return 1;
        }

        // dates must be equal
        return 0;
    }

    /**
     * Unique identifier
     */
    @JsonProperty({name: "GUID"})
        guid: string;

    /**
     * Projektschlüssel
     */
    @JsonProperty({name: "PROJ_ID"})
        id: number | null = null;

    /**
     * Projektnummer vif
     */
    @JsonProperty({name: "PROJ_UUID"})
        uuid: string | null = null;

    /**
     * Projektname
     *
     * @example: Gefahrenkarte Hochdorf
     */
    @JsonProperty({name: "PROJ_NAME"})
        name: string | null = null;

    /**
     * Projekttyp
     * Beschreibt, ob es sich um eine Gefahrenbeurteilung mit der Beurteilungstiefe Gefahrenkarte und/oder
     * Gefahrenhinweiskarte handelt
     *
     * @example: Gefahrenkarte Hochdorf
     */
    @JsonProperty({name: "PROJ_TYP"})
        typeId: number | null = null;

    /**
     * Projektgrund
     * Beschreibt den Grund für die Projektauslösung
     *
     * @example: Ersterhebung, Neubeurteilung Wasserprozesse nach Massnahmen
     */
    @JsonProperty({name: "PROJ_GRUND"})
        reason: string | null = null;

    /**
     * Auftragnehmer
     * Auftragnehmer des Projekts
     *
     * @example: Muster AG, 6000 Luzern
     */
    @JsonProperty({name: "AUFT_NEHM"})
        contractorAsText: string | null = null;

    /**
     * Auftragnehmer ID
     * Verknüpfung zu Auftragnehmer des Projekts
     *
     */
    @JsonProperty({name: "AUFTRAGNEHMER_ID"})
        contractorId: number | null = null;

    /**
     * Projektstatus
     *
     */
    status: string;
    @JsonProperty({name: "STATUS"})
        statusId: number | null = null;

    /**
     * Gefährdungskarte Oberflächenabfluss
     * When true, 'Gefährdungskarte Oberflächenabfluss' is calculated
     */
    @JsonProperty({
        name: "GKOA",
        beforeDeserialize: esriToBoolean,
        afterSerialize: booleanToEsri
    })
        gkoa: boolean;

    /**
     * Gefahrenhinweiskarte Wasser
     * When true, 'Gefahrenhinweiskarte Wasser' is calculated
     */
    @JsonProperty({
        name: "GHKW",
        beforeDeserialize: esriToBoolean,
        afterSerialize: booleanToEsri
    })
        ghkw: boolean;

    /**
     * The running process for this project.
     * Every project has a running process. The process is initialized when creating the project
     */
    process: ProjectProcess | undefined | null;

    /**
     * The related contractor
     */
    contractor: Contractor | undefined | null;

    /**
     * Related municipalities
     */
    municipalities: Municipality[] | undefined | null;


    constructor(props?: ProjectProps) {
        super();
        if (props) {
            Object.assign(this, props);
        }
        makeObservable(this, {
            objectId: observable, // do observe to reload when object is saved
            guid: observable,
            id: observable,
            uuid: observable,
            name: observable,
            reason: observable,
            typeId: observable,
            contractorAsText: observable,
            contractorId: observable,
            status: observable,
            statusId: observable,
            gkoa: observable,
            ghkw: observable,

            process: observable,
            contractor: observable,
            isAlterable: computed,
            hasPartialOrdersGk: computed,
            hasPartialOrdersGhk: computed,
            hasPartialOrdersRisk: computed,

            creator: observable,
            createdAt: observable,
            editor: observable,
            updatedAt: observable,
            isNewRecord: computed,
            isClosed: computed,
            closedOn: computed
        });
    }

    get comparableAttributes(): string[] {
        return Project.comparableAttributes;
    }

    /**
     * If project properties are allowed to change.
     * Depends on current process status.
     */
    get isAlterable(): boolean {
        return this.process?.isProjectAlterable === true;
    }

    /**
     * If the process is in a closed state
     */
    get isClosed(): boolean {
        return this.process?.isClosed === true;
    }

    /**
     * If project type has partial orders for Gefahrenkarte
     */
    get hasPartialOrdersGk(): boolean {
        return this.typeId === 1 || this.typeId === 3;
    }

    /**
     * If project type has partial orders for Gefahrehinweiskarte
     */
    get hasPartialOrdersGhk(): boolean {
        return this.typeId === 2 || this.typeId === 3;
    }
    /**
     * If project type has partial orders for Risikoübersicht
     */
    get hasPartialOrdersRisk(): boolean {
        return this.typeId === 4;
    }

    get isTypeRisk(): boolean {
        return this.typeId === 4;
    }

    get closedOn(): Date | undefined {
        return this.process ? this.process.closedOn : undefined;
    }
}

